import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { z } from "zod";

export type AuthResult<T = undefined> =
  | (T extends undefined ? { ok: true } : { ok: true; data: T })
  | { ok: false; error: string };

const EmailSchema = z.string().email().min(3).max(254);

// Connexion réservée aux invités (accueil des testeurs, 2026-10-29) : un compte
// naît de la fiche admin /accueil-testeurs, jamais de la page de connexion.
// `shouldCreateUser: false` n'est qu'une politesse côté client — le verrou est
// « Allow new users to sign up » désactivé côté Supabase. Les deux refus de
// GoTrue (`otp_disabled` : courriel inconnu avec shouldCreateUser false ;
// `signup_disabled` : inscriptions fermées) reçoivent NOTRE phrase.
// Elle oriente d'abord vers le courriel pro (2026-11-03) : le compte naît du
// courriel pro de la fiche, alors que TestFlight vient de faire utiliser
// l'adresse de l'identifiant Apple, souvent personnelle.
export const SANS_INVITATION =
  "Ce courriel n'a pas accès à Écoute. Essaie ton courriel professionnel, pas celui de ton identifiant Apple. Sinon, écris à thierry.poitras@flambeau.quebec.";

function estRefusSansCompte(error: { code?: string; message: string }): boolean {
  return (
    error.code === "otp_disabled" ||
    error.code === "signup_disabled" ||
    /signups not allowed/i.test(error.message)
  );
}

interface SendMagicLinkOptions {
  redirectTo?: string;
}

export async function sendMagicLink(
  client: SupabaseClient,
  email: string,
  options: SendMagicLinkOptions = {},
): Promise<AuthResult> {
  const parsed = EmailSchema.safeParse(email);
  if (!parsed.success) {
    return { ok: false, error: "Adresse courriel invalide" };
  }

  try {
    const { error } = await client.auth.signInWithOtp({
      email: parsed.data,
      options: {
        shouldCreateUser: false,
        ...(options.redirectTo ? { emailRedirectTo: options.redirectTo } : {}),
      },
    });

    if (error) {
      return { ok: false, error: estRefusSansCompte(error) ? SANS_INVITATION : error.message };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}

// Accès de revue Apple (TestFlight externe, 2026-11-03) : la revue bêta exige un
// compte de démo, et un réviseur ne peut pas recevoir notre lien magique. Une
// seule adresse se connecte par mot de passe ; le domaine n'est volontairement
// pas `flambeau.quebec`, sinon `is_admin()` en ferait un admin. L'adresse n'est
// pas un secret ; le mot de passe vit seulement dans Supabase et App Store
// Connect. Interrupteur : `banned_until` (docs/deploiement.md).
export const COMPTE_REVUE = "revue-apple@ecoute.flambeau.quebec";

export function isReviewEmail(raw: string): boolean {
  return raw.trim().toLowerCase() === COMPTE_REVUE;
}

export async function signInWithReviewPassword(
  client: SupabaseClient,
  email: string,
  password: string,
): Promise<AuthResult> {
  if (!isReviewEmail(email)) {
    return { ok: false, error: "Connexion par mot de passe réservée au compte de revue." };
  }
  try {
    const { error } = await client.auth.signInWithPassword({ email: COMPTE_REVUE, password });
    if (error) {
      if (error.code === "user_banned") return { ok: false, error: "Ce compte est désactivé." };
      if (error.code === "invalid_credentials") {
        return { ok: false, error: "Courriel ou mot de passe invalide." };
      }
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}

export async function signOut(client: SupabaseClient): Promise<AuthResult> {
  try {
    const { error } = await client.auth.signOut();
    if (error) return { ok: false, error: error.message };
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}

export async function getCurrentSession(
  client: SupabaseClient,
): Promise<AuthResult<Session | null>> {
  try {
    const { data, error } = await client.auth.getSession();
    if (error) return { ok: false, error: error.message };
    return { ok: true, data: data?.session ?? null };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : "Erreur inconnue" };
  }
}
